import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/it';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import ApiService from '../services/api';
import { GiornoCalendario, StatisticheMese, EventoCalendario, PuntoVendita } from '../types';
import {
  Calendar as CalendarIcon, Filter, BarChart3, Thermometer,
  Sun, Moon, Cloud, CloudSun, CloudRain, CloudSnow, CloudLightning,
  CloudDrizzle, CloudFog, Droplets
} from 'lucide-react';
import './CalendarioProduzionePage.css';

// Configurazione localizzazione
moment.locale('it');
const localizer = momentLocalizer(moment);

// Messaggi in italiano per il calendario
const messages = {
  allDay: 'Tutto il giorno',
  previous: '◀',
  next: '▶',
  today: 'Oggi',
  month: 'Mese',
  week: 'Settimana',
  day: 'Giorno',
  agenda: 'Agenda',
  date: 'Data',
  time: 'Ora',
  event: 'Evento',
  noEventsInRange: 'Nessun evento in questo periodo',
  showMore: (total: number) => `+ Altri ${total}`
};

// Mappa le icone meteo OpenWeatherMap su icone lucide
const weatherIconComponents: { [key: string]: React.ComponentType<any> } = {
  '01d': Sun, '01n': Moon, '02d': CloudSun, '02n': Cloud,
  '03d': Cloud, '03n': Cloud, '04d': Cloud, '04n': Cloud,
  '09d': CloudDrizzle, '09n': CloudDrizzle, '10d': CloudRain, '10n': CloudRain,
  '11d': CloudLightning, '11n': CloudLightning, '13d': CloudSnow, '13n': CloudSnow,
  '50d': CloudFog, '50n': CloudFog
};

const getWeatherIcon = (iconCode: string, size: number = 16): React.ReactElement => {
  const IconComponent = weatherIconComponents[iconCode] || CloudSun;
  return <IconComponent size={size} style={{ flexShrink: 0 }} />;
};

// Funzione per ottenere il colore basato sul volume di produzione
const getProduzioneColor = (kgTotale: number, maxKg: number): string => {
  if (kgTotale === 0) return '#e9ecef'; // Un grigio leggermente più scuro per lo sfondo vuoto
  const intensity = Math.min(kgTotale / maxKg, 1);
  
  // Scala di colori dal verde acqua (bassa produzione) al verde scuro (alta produzione)
  const hue = 160 - (intensity * 40); // da 160 (verde acqua) a 120 (verde pieno)
  const saturation = 40 + (intensity * 30); // da 40% a 70%
  const lightness = 75 - (intensity * 35); // da 75% a 40%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

interface CalendarioProduzionPageProps {
  onNavigate?: (route: string) => void;
}

const CalendarioProduzionePage: React.FC<CalendarioProduzionPageProps> = ({ onNavigate }) => {
  // Stati
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<View>('month');
  const [giorni, setGiorni] = useState<GiornoCalendario[]>([]);
  const [statistiche, setStatistiche] = useState<StatisticheMese | null>(null);
  const [puntiVendita, setPuntiVendita] = useState<PuntoVendita[]>([]);
  const [filtroPuntoVendita, setFiltroPuntoVendita] = useState<number | null>(null);
  const [dettaglioGiorno, setDettaglioGiorno] = useState<GiornoCalendario | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Carica punti vendita
  useEffect(() => {
    const loadPuntiVendita = async () => {
      try {
        const data = await ApiService.getPuntiVendita();
        setPuntiVendita(data);
      } catch (error) {
        console.error('Errore caricamento punti vendita:', error);
      }
    };
    
    loadPuntiVendita();
  }, []);

  // Funzione per refresh manuale
  const refreshCalendario = useCallback(() => {
    // Trigger del useEffect forzando un nuovo timestamp
    setCurrentDate(new Date());
  }, []);

  // Carica dati quando cambiano la data o il filtro punto vendita
  useEffect(() => {
    console.log('🔧 useEffect triggered:', { 
      currentDateValue: currentDate.getTime(), 
      filtroPuntoVendita,
      loading
    });
    
    const loadCalendarioData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const anno = currentDate.getFullYear();
        const mese = currentDate.getMonth() + 1;
        
        console.log('🔄 Caricamento dati calendario:', { anno, mese, filtroPuntoVendita, view });
        
        if (view === 'agenda') {
          // Per l'agenda, carichiamo 3 mesi di dati (precedente, corrente, successivo)
          const mesiDaCaricare = [mese - 1, mese, mese + 1].map(m => {
            let annoTarget = anno;
            let meseTarget = m;
            
            if (m <= 0) {
              annoTarget = anno - 1;
              meseTarget = 12 + m;
            } else if (m > 12) {
              annoTarget = anno + 1;
              meseTarget = m - 12;
            }
            
            return { anno: annoTarget, mese: meseTarget };
          });
          
          console.log('📅 Mesi da caricare per agenda:', mesiDaCaricare);
          
          const promeseDati = mesiDaCaricare.map(({ anno: a, mese: m }) =>
            ApiService.getCalendarioMese(a, m, filtroPuntoVendita || undefined)
              .catch(err => {
                console.warn(`Errore caricamento ${a}/${m}:`, err);
                return [];
              })
          );
          
          const [statisticheData, ...datiMesi] = await Promise.all([
            ApiService.getStatisticheMese(anno, mese, filtroPuntoVendita || undefined),
            ...promeseDati
          ]);
          
          const tuttiGiorni = datiMesi.flat();
          
          console.log('📅 Dati agenda caricati:', {
            mesiCaricati: mesiDaCaricare,
            giorniTotali: tuttiGiorni.length
          });
          
          setGiorni(tuttiGiorni);
          setStatistiche(statisticheData);
        } else {
          // Per la vista mese, caricamento standard
          const [giorniData, statisticheData] = await Promise.all([
            ApiService.getCalendarioMese(anno, mese, filtroPuntoVendita || undefined),
            ApiService.getStatisticheMese(anno, mese, filtroPuntoVendita || undefined)
          ]);
          
          console.log('📅 Dati mese caricati:', {
            anno,
            mese,
            giorniCount: giorniData.length
          });
          
          setGiorni(giorniData);
          setStatistiche(statisticheData);
        }
      } catch (err: any) {
        console.error('❌ Errore caricamento calendario:', err);
        setError(err.response?.data?.error || 'Errore nel caricamento dei dati');
      } finally {
        setLoading(false);
      }
    };

    // Debounce di 300ms per evitare troppe richieste
    const timeoutId = setTimeout(() => {
      loadCalendarioData();
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch con debounce solo al cambio di data/filtro/vista; getTime() evita rifetch con date equivalenti
  }, [currentDate.getTime(), filtroPuntoVendita, view]); // Aggiungiamo view alle dipendenze

  // Calcola valore massimo di kg per la scala colori
  const maxKg = useMemo(() => {
    return Math.max(...giorni.map(g => g.produzioni.kg_totale), 1);
  }, [giorni]);

  // Converte i dati del calendario in eventi per React Big Calendar
  const events = useMemo((): EventoCalendario[] => {
    const eventiCalendario = giorni.map(giorno => {
      // Parsing sicuro della data
      const dataString = giorno.data;
      let startDate, endDate;
      
      // Se la data è già in formato ISO (contiene T), usala direttamente
      if (dataString.includes('T')) {
        startDate = new Date(dataString);
        endDate = new Date(dataString);
        // Imposta l'ora di fine a 23:59:59
        endDate.setHours(23, 59, 59, 999);
      } else {
        // Se è solo una data (YYYY-MM-DD), aggiungi l'ora
        startDate = new Date(dataString + 'T00:00:00');
        endDate = new Date(dataString + 'T23:59:59');
      }
      
      // Per l'agenda, gli eventi devono avere orari specifici
      if (view === 'agenda') {
        // Imposta orario di produzione realistico (10:00 - 18:00)
        startDate.setHours(10, 0, 0, 0);
        endDate.setHours(18, 0, 0, 0);
      } else {
        // Per la vista mese, tutto il giorno
        endDate.setHours(23, 59, 59, 999);
      }
      
      // Verifica che le date siano valide
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error('❌ Data non valida:', { dataString, startDate, endDate });
        return null;
      }
      
      // Crea titolo appropriato per la vista
      const title = view === 'agenda' 
        ? `Produzione: ${giorno.produzioni.kg_totale.toFixed(1)}kg (${giorno.produzioni.num_produzioni} batch)${giorno.meteo.temperatura_media ? ` - ${giorno.meteo.temperatura_media.toFixed(1)}°C` : ''}`
        : ''; // Titolo vuoto per vista mese, usiamo il custom component
      
      return {
        id: giorno.data,
        title: title,
        start: startDate,
        end: endDate,
        allDay: view !== 'agenda', // Solo allDay per vista mese
        resource: giorno
      };
    }).filter((evento): evento is EventoCalendario => evento !== null); // Type guard per rimuovere null
    
    console.log('📅 Eventi calendario generati:', {
      giorniCount: giorni.length,
      eventiCount: eventiCalendario.length,
      eventi: eventiCalendario.map(e => ({
        id: e.id,
        start: e.start?.toISOString?.() || 'Invalid Date',
        end: e.end?.toISOString?.() || 'Invalid Date',
        kg_totale: e.resource.produzioni?.kg_totale,
        num_produzioni: e.resource.produzioni?.num_produzioni,
        temperatura: e.resource.meteo?.temperatura_media,
        punti_vendita: e.resource.produzioni?.punti_vendita
      }))
    });
    
    return eventiCalendario;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- eventi rigenerati volutamente solo al cambio dei dati, non della vista
  }, [giorni]);

  // Componente personalizzato per gli eventi del calendario
  const EventComponent = ({ event }: { event: EventoCalendario }) => {
    const giorno = event.resource;
    const backgroundColor = getProduzioneColor(giorno.produzioni.kg_totale, maxKg);
    
    return (
      <div 
        className="calendario-evento"
        style={{ backgroundColor }}
      >
        {/* Dati meteo */}
        {giorno.meteo.icona && (
          <div className="evento-meteo">
            <span className="evento-weather-icon">
              {getWeatherIcon(giorno.meteo.icona)}
            </span>
            {giorno.meteo.temperatura_media && (
              <span className="evento-temperatura">
                {giorno.meteo.temperatura_media.toFixed(0)}°
              </span>
            )}
          </div>
        )}
        
        {/* Dati produzione */}
        <div className="evento-produzione">
          <div className="evento-kg">
            <span>{giorno.produzioni.kg_totale.toFixed(1)}</span>
            <small>kg</small>
          </div>
          {giorno.produzioni.num_punti_vendita > 0 && (
            <div className="evento-punti">
              {giorno.produzioni.num_punti_vendita} PV
            </div>
          )}
        </div>

        {/* Indicatori precipitazioni */}
        {giorno.meteo.precipitazioni && giorno.meteo.precipitazioni > 0 && (
          <div className="evento-pioggia"><CloudRain size={12} /></div>
        )}
      </div>
    );
  };

  // Gestione cambio data
  const handleNavigate = (date: Date) => {
    setCurrentDate(date);
  };

  // Gestione cambio vista
  const handleViewChange = (newView: View) => {
    console.log('🔄 Cambio vista calendario:', { oldView: view, newView });
    setView(newView);
    // Il ricaricamento avverrà automaticamente grazie al useEffect con dipendenza [view]
  };

  if (loading) {
    return (
      <div className="calendario-loading">
        <div className="loading-spinner"></div>
        <p>Caricamento calendario...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="calendario-error">
        <h3>Errore</h3>
        <p>{error}</p>
        <button onClick={refreshCalendario} className="btn btn-primary">
          Riprova
        </button>
      </div>
    );
  }

  return (
    <div className="calendario-produzione-page">
      {/* Header */}
      <div className="calendario-header">
        <div className="header-title">
          <CalendarIcon size={32} />
          <div>
            <h1>Calendario Produzione & Meteo</h1>
            <p>Panoramica mensile di produzione e condizioni meteorologiche</p>
          </div>
        </div>
        
        {/* Filtri */}
        <div className="calendario-filtri">
          <div className="filtro-punto-vendita">
            <Filter size={16} />
            <select 
              value={filtroPuntoVendita || ''} 
              onChange={(e) => setFiltroPuntoVendita(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Tutti i punti vendita</option>
              {puntiVendita.map(pv => (
                <option key={pv.id} value={pv.id}>{pv.nome}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Statistiche mensili */}
      {statistiche && (
        <div className="calendario-stats">
          <div className="stat-card">
            <BarChart3 size={24} />
            <div className="stat-content">
              <div className="stat-value">{statistiche.kg_totale_mese.toFixed(1)}kg</div>
              <div className="stat-label">Totale Mese</div>
            </div>
          </div>
          
          <div className="stat-card">
            <CalendarIcon size={24} />
            <div className="stat-content">
              <div className="stat-value">{statistiche.giorni_con_produzione}</div>
              <div className="stat-label">Giorni Attivi</div>
            </div>
          </div>
          
          <div className="stat-card">
            <Thermometer size={24} />
            <div className="stat-content">
              <div className="stat-value">
                {statistiche.meteo.temp_media_mese?.toFixed(1) || 'N/D'}°C
              </div>
              <div className="stat-label">Temp. Media</div>
            </div>
          </div>
          
          <div className="stat-card">
            <Droplets size={24} />
            <div className="stat-content">
              <div className="stat-value">
                {statistiche.meteo.precipitazioni_totali_mese?.toFixed(1) || '0'}mm
              </div>
              <div className="stat-label">Precipitazioni</div>
            </div>
          </div>
        </div>
      )}

      {/* Legenda */}
      <div className="calendario-legenda">
        <h4>Legenda:</h4>
        <div className="legenda-items">
          <div className="legenda-item">
            <div className="legenda-colore" style={{ backgroundColor: getProduzioneColor(0, maxKg) }}></div>
            <span>Nessuna produzione</span>
          </div>
          <div className="legenda-item">
            <div className="legenda-colore" style={{ backgroundColor: getProduzioneColor(maxKg * 0.3, maxKg) }}></div>
            <span>Produzione bassa</span>
          </div>
          <div className="legenda-item">
            <div className="legenda-colore" style={{ backgroundColor: getProduzioneColor(maxKg * 0.7, maxKg) }}></div>
            <span>Produzione media</span>
          </div>
          <div className="legenda-item">
            <div className="legenda-colore" style={{ backgroundColor: getProduzioneColor(maxKg, maxKg) }}></div>
            <span>Produzione alta</span>
          </div>
        </div>
      </div>

      {/* Calendario */}
      <div className="calendario-container">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          messages={messages}
          view={view}
          onView={handleViewChange}
          date={currentDate}
          onNavigate={handleNavigate}
          views={['month', 'agenda']}
          components={{
            event: EventComponent,
          }}
          onSelectEvent={(event) => {
            setDettaglioGiorno(event.resource);
            setShowModal(true);
          }}
          eventPropGetter={(event) => ({
            style: {
              backgroundColor: getProduzioneColor(event.resource.produzioni.kg_totale, maxKg),
            },
          })}
        />
      </div>

      {/* Modal dettagli giorno */}
      {showModal && dettaglioGiorno && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-dettaglio" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Dettagli del {moment(dettaglioGiorno.data).format('DD/MM/YYYY')}</h3>
              <button onClick={() => setShowModal(false)} className="modal-close">×</button>
            </div>
            
            <div className="modal-content">
              <div className="dettaglio-sezioni">
                {/* Sezione Produzione */}
                <div className="dettaglio-sezione">
                  <h4>Produzione</h4>
                  <div className="dettaglio-stats">
                    <div className="dettaglio-stat">
                      <span className="stat-label">Volume totale:</span>
                      <span className="stat-value">{dettaglioGiorno.produzioni.kg_totale.toFixed(2)} kg</span>
                    </div>
                    <div className="dettaglio-stat">
                      <span className="stat-label">Numero produzioni:</span>
                      <span className="stat-value">{dettaglioGiorno.produzioni.num_produzioni}</span>
                    </div>
                    <div className="dettaglio-stat">
                      <span className="stat-label">Punti vendita attivi:</span>
                      <span className="stat-value">{dettaglioGiorno.produzioni.num_punti_vendita}</span>
                    </div>
                    {dettaglioGiorno.produzioni.punti_vendita.length > 0 && (
                      <div className="dettaglio-stat">
                        <span className="stat-label">Punti vendita:</span>
                        <span className="stat-value">{dettaglioGiorno.produzioni.punti_vendita.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sezione Meteo */}
                <div className="dettaglio-sezione">
                  <h4>Condizioni Meteo</h4>
                  {dettaglioGiorno.meteo.temperatura_media ? (
                    <div className="dettaglio-stats">
                      <div className="dettaglio-meteo-principale">
                        {dettaglioGiorno.meteo.icona && (
                          <span className="dettaglio-weather-icon">
                            {getWeatherIcon(dettaglioGiorno.meteo.icona, 32)}
                          </span>
                        )}
                        <div className="temperatura-dettaglio">
                          <span className="temp-media">{dettaglioGiorno.meteo.temperatura_media.toFixed(1)}°C</span>
                          {dettaglioGiorno.meteo.condizione && (
                            <span className="condizione">{dettaglioGiorno.meteo.condizione}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="dettaglio-meteo-grid">
                        {dettaglioGiorno.meteo.temperatura_min && (
                          <div className="dettaglio-stat">
                            <span className="stat-label">Min:</span>
                            <span className="stat-value">{dettaglioGiorno.meteo.temperatura_min.toFixed(1)}°C</span>
                          </div>
                        )}
                        {dettaglioGiorno.meteo.temperatura_max && (
                          <div className="dettaglio-stat">
                            <span className="stat-label">Max:</span>
                            <span className="stat-value">{dettaglioGiorno.meteo.temperatura_max.toFixed(1)}°C</span>
                          </div>
                        )}
                        {dettaglioGiorno.meteo.umidita && (
                          <div className="dettaglio-stat">
                            <span className="stat-label">Umidità:</span>
                            <span className="stat-value">{dettaglioGiorno.meteo.umidita.toFixed(0)}%</span>
                          </div>
                        )}
                        {dettaglioGiorno.meteo.precipitazioni !== null && (
                          <div className="dettaglio-stat">
                            <span className="stat-label">Precipitazioni:</span>
                            <span className="stat-value">{dettaglioGiorno.meteo.precipitazioni.toFixed(1)}mm</span>
                          </div>
                        )}
                        {dettaglioGiorno.meteo.pressione && (
                          <div className="dettaglio-stat">
                            <span className="stat-label">Pressione:</span>
                            <span className="stat-value">{dettaglioGiorno.meteo.pressione.toFixed(0)} hPa</span>
                          </div>
                        )}
                        {dettaglioGiorno.meteo.vento_velocita && (
                          <div className="dettaglio-stat">
                            <span className="stat-label">Vento:</span>
                            <span className="stat-value">{dettaglioGiorno.meteo.vento_velocita.toFixed(1)} m/s</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="no-meteo">Dati meteo non disponibili</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={() => {
                  setShowModal(false);
                  onNavigate?.('storico-produzione');
                }}
                className="btn btn-primary"
              >
                Vai al dettaglio produzione
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarioProduzionePage;
